import assert from '@/sui/tools/assert'
import checker from '@/sui/tools/checker'

const states = {
  idle: 0,
  running: 1,
  valid: 2,
  invalid: 3,
  pending: 4
}

class StepState {
  constructor () {
    this.resetState()
  }

  resetState () {
    this._state = states.idle
    this._msg = null
    this._report = null
  }

  get state () {
    return this._state
  }

  setState (newState) {
    this._state = newState
  }

  setMsg (msg) {
    this._msg = msg
  }

  answer (a) {
    assert.isBoolean(a)
    if (a) {
      this.setState(states.valid)
      this.setMsg(`<b>Étape validée par l'utilisateur.</b>`)
      return
    }
    this.setErrorState(
      `<b>Procédure stoppée manuellement par l'utilisateur.</b>`
    )
  }

  setErrorState (msg) {
    this._msg = msg
    this._state = states.invalid
  }

  updateState (vm, result) {
    if (result.success) {
      this._state = states.valid
      this._msg = result.msg
    } else {
      if (result.askConfirmation) {
        result.success = true
        this._state = states.pending
        this._report = result.report
      } else {
        this.setErrorState(result.error)
      }
    }
  }
}

class StepIterator {
  constructor (steps) {
    this.current = -1
    this.steps = steps
    this.started = false
    this.stepStates = []
    this.progress = {
      value: 0
    }

    const c = checker()
    for (let i in steps) {
      this.stepStates.push(new StepState())
      const step = steps[i]
      c.check(step.run != null, `missing run method on step ${i}.`)
      c.check(step.setError != null, `missing setError method on step ${i}.`)
    }
    c.end()
  }

  reset () {
    this.started = false
    this.progress = {
      value: 0
    }
    for (let i in this.steps) {
      const st = this.stepStates[i]
      st.resetState()
    }
  }

  startNextStep (parent, data) {
    setTimeout(this.startStep.bind(this, parent, data, this.current + 1), 10)
  }

  isPossibleToContinueWithErrors (data) {
    const step = this.step
    return step.continue && step.continue(data)
  }

  endAllSteps () {
    this.current = this.steps.length - 1
  }

  launchSelectNextStep (result, parent, data, vm) {
    vm.selectNextStep(result, parent, data, vm)
  }

  selectNextStep (result, parent, data, vm) {
    const state = vm.stepState.state

    if (state === states.pending) {
      const vm = this
      setTimeout(this.launchSelectNextStep, 200, result, parent, data, vm)
    } else if (
      !this.completed &&
        (
          (state === states.valid && result.success) ||
          (state === states.invalid &&
              this.isPossibleToContinueWithErrors(data) &&
              !result.askConfirmation)
        )
    ) {
      vm.startNextStep(parent, data)
    } else {
      vm.endAllSteps()
    }
  }

  start (parent) {
    this.reset()
    const data = {}
    this.started = true
    this.startStep(parent, data, 0)
  }

  get completed () {
    return this.current === this.steps.length - 1
  }

  get step () {
    assert(this.current >= 0 && this.current < this.steps.length)
    return this.steps[this.current]
  }

  get stepState () {
    return this.stepStates[this.current]
  }

  startStep (parent, data, next) {
    assert(next >= 0 && next < this.steps.length)
    this.current = next
    this.stepState.setState(states.running)

    const vm = this
    return this.run(parent, data).then(
      (result) => {
        vm.stepState.updateState(vm, result)
        vm.selectNextStep(result, parent, data, vm)
      }
    ).catch(
      (e) => {
        const err = vm.step.setError(e)
        vm.stepState.setErrorState(err)
        vm.endAllSteps()
      }
    )
  }

  run (parent, data) {
    const vm = this
    const start = Date.now()
    let iteration = 0
    const object = this.step
    if (object.promise) {
      return object.run(parent, data, iteration)
    }
    return new Promise(iterate)

    function iterate (resolve, reject) {
      if (!vm.started) {
        return
      }

      const result = object.run(parent, data, iteration++)
      if (result.end) {
        resolve(result)
      } else if (object.timeout && (Date.now() - start >= object.timeout)) {
        reject(new Error('TIMEOUT'))
      } else {
        setTimeout(iterate.bind(vm, resolve, reject), 30)
      }
    }
  }
}

StepIterator.states = states

export default StepIterator
